import React, { useState } from "react";
import { Eye, Download, PlayCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import UserAvatar from "@/components/UserAvatar";

const META = {
  open: { label: "Open", icon: Eye },
  view: { label: "Tonton", icon: PlayCircle },
  download: { label: "Download", icon: Download },
};

const fmt = (iso) => new Date(iso).toLocaleString("ms-MY", { dateStyle: "medium", timeStyle: "short" });

function UsersDialog({ target, onClose }) {
  const [rows, setRows] = useState(null);
  React.useEffect(() => {
    if (!target) return;
    setRows(null);
    api.get(`/admin/content-stats/${target.kind}/${target.id}`, { params: { action: target.action } })
      .then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [target]);
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="content-stats-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-base pr-6">
            {target && `${META[target.action].label} · ${target.title}`}
          </DialogTitle>
        </DialogHeader>
        {!rows ? <div className="py-8 grid place-items-center"><Loader2 className="animate-spin text-[#00b4ff]" /></div>
          : rows.length === 0 ? <div className="py-6 text-sm text-slate-500 text-center" data-testid="content-stats-empty">Belum ada lagi.</div>
          : (
            <div className="max-h-[60vh] overflow-y-auto -mx-2 divide-y divide-slate-100" data-testid="content-stats-list">
              {rows.map((r) => (
                <div key={r.user_id} className="flex items-center gap-3 px-2 py-2.5" data-testid={`content-stats-row-${r.user_id}`}>
                  <UserAvatar path={r.profile_picture_path} size={34} fallback={<span className="text-xs font-bold">{r.name[0]?.toUpperCase()}</span>} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{r.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{r.email}{r.telegram_username ? ` · @${r.telegram_username.replace(/^@/, "")}` : ""}</div>
                    <div className="text-[10px] font-mono-plex text-slate-400">Terakhir: {fmt(r.last_at)}</div>
                  </div>
                  <span className="shrink-0 px-2 py-1 rounded-full bg-sky-50 text-[#0090cc] text-[11px] font-mono-plex font-bold">{r.count}×</span>
                </div>
              ))}
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}

export default function ContentStatsBar({ kind, item, stats, canDownload = true }) {
  const [target, setTarget] = useState(null);
  const actions = kind === "ebook" ? ["open", "download"] : canDownload ? ["view", "download"] : ["view"];
  return (
    <>
      <div className="mt-3 flex flex-wrap gap-2" data-testid={`content-stats-${item.id}`}>
        {actions.map((a) => {
          const s = stats?.[a] || { unique: 0, total: 0 };
          const Icon = META[a].icon;
          return (
            <button key={a} data-testid={`content-stats-${a}-${item.id}`}
                    onClick={() => setTarget({ kind, id: item.id, action: a, title: item.title })}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-sky-100 bg-sky-50/60 hover:bg-sky-100 text-[11px] text-slate-700"
                    style={{ transition: "background-color 0.2s ease" }}>
              <Icon size={12} className="text-[#00b4ff]" />
              <span className="font-semibold">{META[a].label}</span>
              <span className="font-mono-plex"><b>{s.unique}</b> orang · <b>{s.total}</b> kali</span>
            </button>
          );
        })}
      </div>
      <UsersDialog target={target} onClose={() => setTarget(null)} />
    </>
  );
}
